import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Receipt,
  ChartLineUp,
  Shield,
  Buildings,
  Books,
  Scales,
  Globe,
  Gavel,
  ArrowUpRight,
} from "@phosphor-icons/react";

const services = [
  {
    id: "income-tax",
    title: "Income Tax",
    desc: "Compliance has evolved from an annual return into a full-time function focused on managing the growing information and documentation demands of tax authorities. AACO supports the entire compliance lifecycle — from full-outsourced models to reviewing internal compliance functions.",
    points: [
      "Preparation and filing of corporate tax returns",
      "Corporate tax litigation assistance including representation services",
      "Other compliances — tax registrations, NIL / lower withholding tax certificates, certifications",
    ],
    icon: Receipt,
    span: "lg:col-span-7 lg:row-span-2",
    tone: "dark",
  },
  {
    id: "gst",
    title: "GST",
    desc: "End-to-end GST compliance, advisory and litigation support.",
    points: [
      "Advisory on complex GST matters including tariff classification and eligibility of exemptions",
      "Filing of annual returns (GSTR-9 / 9C) and preparation of reconciliations",
      "Assistance with GST refunds",
      "Due diligence and periodic reviews",
      "Automated GST compliance, timely filings and documentation support",
    ],
    icon: ChartLineUp,
    span: "lg:col-span-5 lg:row-span-2",
    tone: "dark",
  },
  {
    id: "audit",
    title: "Audit & Assurance",
    desc: "Independent, unbiased opinions on the accuracy and completeness of financial statements — across Companies Act, US GAAP, IFRS and Ind AS frameworks.",
    icon: Shield,
    span: "lg:col-span-3",
    tone: "light",
    href: "/services/audit-assurance",
    cta: "Read full",
  },
  {
    id: "roc",
    title: "Company Incorporation & ROC",
    desc: "Pvt. Ltd., LLP, OPC, Section 8 incorporations, annual MCA filings, DIR-3 KYC, charges.",
    icon: Buildings,
    span: "lg:col-span-3",
    tone: "accent",
  },
  {
    id: "books",
    title: "Business Process Outsourcing",
    desc: "Cloud-based books, MIS, payroll and vendor reconciliations tailored to your scale.",
    icon: Books,
    span: "lg:col-span-3",
    tone: "light",
  },
  {
    id: "litigation",
    title: "Litigation Support",
    desc: "Representation and drafting support across direct and indirect tax disputes.",
    points: [
      "Comprehensive support for indirect tax litigation — drafting of legal responses / appeals, and representation before tax / appellate authorities and Tribunal for GST, Customs and erstwhile indirect tax laws",
      "Legal strategy during summons / investigations, drafting writ petitions and applications for advance rulings, and managing litigation using automated solutions",
    ],
    icon: Gavel,
    span: "lg:col-span-3",
    tone: "light",
  },
  {
    id: "advisory",
    title: "Financial & Regulatory Advisory",
    desc: "Capital structure, FEMA, transaction advisory, due diligence and CFO advisory.",
    icon: Scales,
    span: "lg:col-span-4",
    tone: "light",
  },
  {
    id: "foreign-entity",
    title: "Foreign Entity Registration",
    desc: "Pvt. Ltd., LLP, Branch / Liaison / Project Office. FDI, FEMA and RBI compliance for foreign investors entering India.",
    icon: Globe,
    span: "lg:col-span-8",
    tone: "dark",
    href: "/services/foreign-entity-registration",
    cta: "Read full guide",
  },
];

const toneStyles = {
  light: "bg-[#FDFBF7] border-[#D1CEC5] text-[#1B3B2B]",
  dark: "bg-[#1B3B2B] border-[#1B3B2B] text-[#FDFBF7]",
  accent: "bg-[#D4AF37] border-[#B3932E] text-[#1B3B2B]",
};

export default function Services() {
  const navigate = useNavigate();
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  const handleClick = (s) => {
    if (s.href) navigate(s.href);
    else scrollTo("contact");
  };
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-32 bg-[#FDFBF7]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-end mb-16">
          <div className="lg:col-span-7">
            <div className="overline mb-5">/ Services</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 auto-rows-[minmax(220px,auto)] gap-5">
          {services.map((s, idx) => {
            const Icon = s.icon;
            const styles = toneStyles[s.tone];
            return (
              <motion.button
                key={s.id}
                data-testid={`service-card-${s.id}`}
                onClick={() => handleClick(s)}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.55, delay: idx * 0.05, ease: "easeOut" }}
                className={`group text-left border ${styles} ${s.span} p-7 md:p-9 flex flex-col justify-between transition-transform duration-300 hover:-translate-y-1`}
              >
                <div className="flex items-start justify-between">
                  <Icon size={32} weight="duotone" />
                  <ArrowUpRight
                    size={20}
                    className="opacity-60 group-hover:opacity-100 transition-opacity"
                  />
                </div>
                <div className="mt-10">
                  <h3 className="font-serif-display text-2xl md:text-3xl leading-tight">
                    {s.title}
                  </h3>
                  <p
                    className={`mt-3 text-sm leading-relaxed ${
                      s.tone === "dark" ? "text-[#E7E1D2]" : s.tone === "accent" ? "text-[#1B3B2B]" : "text-[#4A5D53]"
                    }`}
                  >
                    {s.desc}
                  </p>
                  {s.points && (
                    <ul
                      className={`mt-4 space-y-2 text-xs leading-relaxed ${
                        s.tone === "dark" ? "text-[#E7E1D2]" : s.tone === "accent" ? "text-[#1B3B2B]" : "text-[#4A5D53]"
                      }`}
                    >
                      {s.points.map((pt) => (
                        <li key={pt} className="flex gap-2 items-start">
                          <span className="text-[#D4AF37] mt-1.5 w-1 h-1 bg-[#D4AF37] rounded-full shrink-0" />
                          <span>{pt}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {s.cta && (
                    <span
                      className={`mt-5 inline-flex items-center gap-1.5 overline text-[10px] ${
                        s.tone === "dark" ? "text-[#D4AF37]" : "text-[#1B3B2B]"
                      }`}
                    >
                      {s.cta} <ArrowUpRight size={12} weight="bold" />
                    </span>
                  )}
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
